// Bake the email hero banner — CLIENT-ONLY (needs a live WebGL organism, which
// only exists in a browser). Composites the REAL rendered organism onto the
// SATURATED organism-hue backdrop (the Figma email hero — the bloom glows as
// glass on its own rich colour field), with the wordmark top-left and, top-right,
// the recipient's avatar + username. One baked PNG. The single place this
// composition is defined, shared by the dashboard uploader + the dev bench.

import {
  representativeOrganismHue,
  hslToRgb,
  chromeState,
  derivePalette,
  type ColorState,
  type RGB,
} from "../organism/color";
import { readableOn } from "../theme";
import { WORDMARK } from "./marks";

const mix = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const rgbCss = (c: RGB) => `rgb(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)})`;
const WHITE: RGB = [1, 1, 1];

/** The saturated organism-hue hero backdrop (Figma #9919c7 / #292dff family): a
 *  rich version of the SAME hue the petals wear. Greens/yellows are deepened +
 *  desaturated (they read neon at the lightness that makes blues/purples rich),
 *  keeping every hue a jewel tone the glowing bloom pops against. */
export function heroBgRgb(color: ColorState): RGB {
  const hue = representativeOrganismHue(color);
  const greenish = hue > 55 && hue < 175;
  return hslToRgb({ h: hue, s: greenish ? 0.7 : 0.82, l: greenish ? 0.32 : 0.45 });
}

export async function bakeHeroCanvas(opts: {
  exportPng: () => string;
  color: ColorState;
  username: string;
  width?: number;
}): Promise<HTMLCanvasElement> {
  const { exportPng, color, username } = opts;
  const w = opts.width ?? 560;
  const bg = heroBgRgb(color);
  const palette = derivePalette(chromeState(color));
  const body = palette.body;
  const ink = readableOn(bg); // wordmark + username on the saturated field (→ white)

  try {
    await Promise.all([
      document.fonts.load("400 32px 'Alte Haas Grotesk'"),
      document.fonts.load("700 32px 'Alte Haas Grotesk'"),
    ]);
  } catch {
    /* fall back to the canvas default sans — still legible */
  }

  const scale = 2;
  const cw = w * scale;
  const ch = Math.round(w * 0.72) * scale;
  const cv = document.createElement("canvas");
  cv.width = cw;
  cv.height = ch;
  const ctx = cv.getContext("2d")!;

  ctx.fillStyle = rgbCss(bg);
  ctx.fillRect(0, 0, cw, ch);

  const drawChrome = () => {
    const S = scale;

    // wordmark, top-left
    const wmScale = (w * 0.19 * S) / 736.618;
    ctx.save();
    ctx.translate(24 * S, 28 * S);
    ctx.scale(wmScale, wmScale);
    ctx.fillStyle = ink;
    ctx.fill(new Path2D(WORDMARK.union));
    ctx.beginPath();
    ctx.arc(WORDMARK.dot.cx, WORDMARK.dot.cy, WORDMARK.dot.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fill(new Path2D(WORDMARK.leaf));
    ctx.restore();

    // avatar (top-right): a clean light disc with the uppercase initial in the
    // organism colour — reads crisply on the saturated field, and still theirs.
    const r = 19 * S;
    const cx = cw - 24 * S - r;
    const cy = 28 * S + r;
    ctx.fillStyle = ink === "#ffffff" ? "#fafcfc" : rgbCss(mix(body, WHITE, 0.86));
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = rgbCss(body);
    ctx.font = `700 ${Math.round(r * 0.95)}px 'Alte Haas Grotesk', sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((username || "n").charAt(0).toUpperCase(), cx, cy + r * 0.06);

    // username, to the LEFT of the avatar
    ctx.fillStyle = ink;
    ctx.globalAlpha = 0.73;
    ctx.font = `400 ${Math.round(16 * S)}px 'Alte Haas Grotesk', sans-serif`;
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText(username || "netti", cx - r - 12 * S, cy);
    ctx.globalAlpha = 1;

    return cv;
  };

  return new Promise((resolve) => {
    const dataUrl = exportPng();
    if (!dataUrl) return resolve(drawChrome());
    const img = new Image();
    img.onload = () => {
      // the organism rises from the bottom, petals fanning up and bleeding off
      // the edges — a big bloom low in the frame (Figma image 61 composition).
      const targetH = ch * 1.5;
      const s = targetH / img.height;
      const dw = img.width * s;
      const dh = img.height * s;
      ctx.drawImage(img, (cw - dw) / 2, ch * 0.9 - dh / 2, dw, dh);
      resolve(drawChrome());
    };
    img.onerror = () => resolve(drawChrome());
    img.src = dataUrl;
  });
}

export function canvasToBlob(cv: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => cv.toBlob((b) => resolve(b), "image/png"));
}
